import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '@/lib/utils';

const getClientLabel = (c) =>
  (c.client_type === 'natural'
    ? `${c.surname || ''} ${c.name || ''}`.trim()
    : (c.company_name || '')
  ).toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Simple, reliable client dropdown using shadcn Select (Radix Select under the hood).
 * Drop-in replacement for callers that previously used a richer combobox.
 *
 * Props:
 * - value: current client id (or '' / null for "none")
 * - onChange: (id: string) => void  — receives '' when "none" selected
 * - clients: array of client objects
 * - placeholder: trigger placeholder
 * - allowNone: if true, includes a "Χωρίς πελάτη" option (returns '' on select)
 * - noneLabel: label for the "none" option
 * - className: passed to trigger
 * - testId: data-testid for trigger button
 * - disabled
 * - disableSearch: kept for API compatibility; ignored (no search anyway)
 */
const SearchableClientSelect = ({
  value,
  onChange,
  clients = [],
  placeholder = 'Επιλέξτε πελάτη',
  allowNone = false,
  noneLabel = 'Χωρίς πελάτη',
  className,
  testId,
  disabled,
}) => {
  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) =>
      getClientLabel(a).localeCompare(getClientLabel(b), 'el', { sensitivity: 'base' })
    );
  }, [clients]);

  const NONE_TOKEN = '__none__';

  const triggerValue = value || (allowNone ? NONE_TOKEN : undefined);

  return (
    <Select
      value={triggerValue}
      onValueChange={(val) => onChange(val === NONE_TOKEN ? '' : val)}
      disabled={disabled}
    >
      <SelectTrigger
        data-testid={testId}
        className={cn(
          'h-10 w-full border border-slate-300 bg-white text-sm text-slate-700',
          className
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowNone && (
          <SelectItem value={NONE_TOKEN} className="text-slate-500">
            {noneLabel}
          </SelectItem>
        )}
        {sortedClients.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {getClientLabel(c)}
          </SelectItem>
        ))}
        {sortedClients.length === 0 && !allowNone && (
          <div className="py-6 text-center text-sm text-slate-500">
            Δεν υπάρχουν πελάτες
          </div>
        )}
      </SelectContent>
    </Select>
  );
};

export default SearchableClientSelect;
